import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { usePersistFn } from '@euler/utils/hooks';
import { memo } from 'react';
import {
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSubmitEditingEventData,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { css } from 'styled-components';

const kSearchInputDefaultHeight = 36;

type SearchInputProps = {
  autoCorrect?: boolean;
  autoFocus?: boolean;
  autoCapitalize?: TextInputProps['autoCapitalize'];
  searchInputEditable?: boolean;
  searchText?: string;
  searchPlaceholder?: string;
  searchPlaceholderTextColor?: string;
  style?: StyleProp<ViewStyle>;
  onSearchInputPress?: () => void;
  onSearchInputChange?: (text: string) => void;
  onSearch?: (text: string) => void;
};

export const SearchBar = memo(
  ({
    autoCapitalize,
    autoCorrect,
    autoFocus,
    onSearchInputPress,
    searchText,
    searchPlaceholder,
    searchPlaceholderTextColor,
    searchInputEditable,
    style,
    onSearchInputChange,
    onSearch,
  }: SearchInputProps) => {
    const { rpx } = useResponsiveContext();

    const onSubmitEditing = usePersistFn(
      (_e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
        onSearch?.(searchText ?? '');
      },
    );

    const onKeyPress = usePersistFn(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (Platform.OS !== 'web') return;
        if (e.nativeEvent.key === 'Enter') {
          onSearch?.(searchText ?? '');
        }
      },
    );

    const wrapperCss = css`
      background-color: #fff;
      height: ${rpx(kSearchInputDefaultHeight)}px;
      border-radius: ${rpx(kSearchInputDefaultHeight) / 2}px;
      padding: 0 ${rpx(15)}px;
    `;

    if (searchInputEditable) {
      return (
        <View
          style={style}
          css={`
            ${wrapperCss}
          `}
        >
          <TextInput
            placeholder={searchPlaceholder}
            placeholderTextColor={searchPlaceholderTextColor}
            value={searchText ?? ''}
            onChangeText={onSearchInputChange}
            returnKeyType="search"
            autoFocus={autoFocus}
            autoCapitalize={autoCapitalize}
            autoCorrect={autoCorrect}
            onSubmitEditing={onSubmitEditing}
            clearButtonMode="always"
            onKeyPress={onKeyPress}
            css={`
              flex: 1;
            `}
          />
        </View>
      );
    }
    return (
      <TouchableOpacity
        onPress={onSearchInputPress}
        activeOpacity={0.9}
        css={`
          ${wrapperCss};
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        `}
      >
        <Icon name="search" size={rpx(16)} color="#999" />
        <Typography.Label
          size={rpx(14)}
          light
          color="#999"
          css={`
            margin-left: ${rpx(10)}px;
          `}
        >
          {searchPlaceholder ?? ''}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);
